import React from 'react';
import { Case, If, Switch, Then } from 'react-if';
import { Link } from '@nerdwallet/currency/components/Link';
import { Type } from '@nerdwallet/currency/components/Type';
import useFeatureFlag from '@nerdwallet/features/dist/feature-flags/useFeatureFlag';
import InstitutionIcon from '@components/institution-icon/institution-icon';
import StatusIndicator, {
  Status,
} from '@components/institution-icon/status-indicator';
import ListItem from '@components/list-item/list-item';
import { FEATURE_FLAGS } from '@constants/feature-flags';
import { useFeatureFlagWithLog } from '@lib/hooks/logging-hooks';
import { getLogger } from '@lib/logging';
import { useAnalytics } from '@lib/segment/useAnalytics';
import { getGenericIconForAccountType } from '@lib/selectors/yodlee';
import { FinancialPortal } from '@generated-gql/generated-types';

const PortalProductState = {
  RETRIEVAL_IN_PROGRESS: 'RETRIEVAL_IN_PROGRESS',
};

type FinancialInstitutionProps = {
  onClickUnlink: () => void;
  onClickSeeDetails: () => void;
  portal?: FinancialPortal;
};

type FinancialInstitutionContentProps = {
  portal: FinancialPortal;
  onClickUnlink: (portal: FinancialPortal) => void;
  onClickSeeDetails: () => void;
  isWithinQuestionnaire: boolean;
};

const logger = getLogger('FinancialInstitutions');

const FinancialInstitutionContent: React.FC<
  FinancialInstitutionContentProps
> = ({ portal, onClickUnlink, isWithinQuestionnaire, onClickSeeDetails }) => {
  const { data: plaidModalFeatureFlag } = useFeatureFlagWithLog(
    logger,
    FEATURE_FLAGS.PLAID_MODAL_MIGRATION,
  );

  const omitActionLinks = !plaidModalFeatureFlag?.value;

  const { segmentTrack } = useAnalytics();
  const { data: delistedInstitutionFeature } = useFeatureFlag({
    featureFlagName: FEATURE_FLAGS.FINANCIAL_INSTITUTION_DELISTING,
  });

  const { authState, marketplaceInstitution, migrationDetails } = portal;
  const isPortalMigrating = migrationDetails?.hasUserConfirmed;
  const isRefreshing =
    portal?.isRefreshing ||
    portal?.accountsState === PortalProductState.RETRIEVAL_IN_PROGRESS ||
    portal?.transactionsState === PortalProductState.RETRIEVAL_IN_PROGRESS;
  const isSyncing =
    migrationDetails?.hasUserConfirmed ||
    portal?.isActiveSync ||
    portal?.isRefreshing;
  const isAccountInErrorState = authState === 'ERROR' && !isPortalMigrating;
  const isAccountInMfaRequiredState =
    authState === 'USER_ACTION_REQUIRED' && !isPortalMigrating;
  // Check if the institution is no longer supported by Plaid
  const isAccountDelisted =
    delistedInstitutionFeature?.value &&
    delistedInstitutionFeature?.extraData?.institutions?.find(
      (institution) =>
        institution.sourceInstitutionId === portal.sourceInstitutionId,
    );

  let subtitle = 'Linked';
  if (isAccountDelisted) {
    subtitle = `This institution is no longer supported by Plaid. As a result, we will no longer be able refresh your ${marketplaceInstitution?.name} accounts with the latest account info.`;
  } else if (isAccountInErrorState) {
    subtitle = 'Linking failed';
  } else if (isAccountInMfaRequiredState) {
    subtitle = 'Action required';
  } else if (isRefreshing) {
    subtitle = 'Refreshing...';
  } else if (isSyncing) {
    subtitle = 'Linking...';
  }

  let portalStatus;
  if (isRefreshing || isSyncing) {
    portalStatus = Status.REFRESHING;
  } else if (
    isAccountInErrorState ||
    isAccountInMfaRequiredState ||
    isAccountDelisted
  ) {
    portalStatus = Status.ERROR;
  }

  return (
    <ListItem
      inlineColumnContents={
        <StatusIndicator status={portalStatus}>
          <InstitutionIcon
            altText={marketplaceInstitution?.name}
            src={
              marketplaceInstitution?.logoUrl ??
              getGenericIconForAccountType('BANK')
            }
          />
        </StatusIndicator>
      }
      key={marketplaceInstitution?.id}
      rightIcon={
        <Type size={1}>
          <If condition={!omitActionLinks}>
            <Then>
              <Switch>
                <Case
                  condition={
                    (isAccountInErrorState && isAccountDelisted) ||
                    isAccountInMfaRequiredState
                  }
                >
                  <Link
                    onClick={() => {
                      onClickSeeDetails();
                    }}
                  >
                    See details
                  </Link>
                </Case>
                <Case condition={!isSyncing}>
                  <Link
                    onClick={() => {
                      if (isWithinQuestionnaire) {
                        segmentTrack('Net Worth Account Tile Unlink Clicked');
                      }
                      onClickUnlink(portal);
                    }}
                  >
                    Unlink
                  </Link>
                </Case>
              </Switch>
            </Then>
          </If>
        </Type>
      }
      subtitle={subtitle}
      title={marketplaceInstitution?.name}
    />
  );
};

const FinancialInstitution: React.FC<FinancialInstitutionProps> = ({
  portal,
  onClickUnlink,
  onClickSeeDetails,
}): React.ReactNode => {
  return (
    <React.Fragment>
      <FinancialInstitutionContent
        portal={portal}
        onClickUnlink={onClickUnlink}
        onClickSeeDetails={onClickSeeDetails}
        isWithinQuestionnaire={false}
      />
    </React.Fragment>
  );
};

export default FinancialInstitution;
